import { useMemo, useState } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ReferenceLine,
    Scatter
} from "recharts";
import { getDateAggregationKey } from "../../../utils/getDateAggregationKey";
import { getColor } from "../../../utils/colorUtils";
import { OverlayManager } from "../../../utils/OverlayManager";
import { useOverlayTypes } from "../../../hooks/useOverlayTypes";
import { downsampleMinMax } from '../../../utils/performance';

interface Props {
    data: any[];
    average: number;
    overlays: any[];
    setOverlays: (overlays: any[]) => void;
    tunedResult: any[] | null;
    strategyName: string;
    categorical_labels?: Record<number, string>;
    display_type?: string;
    nature?: string;
    globalStart: string | null;
    globalEnd: string | null;
}

const OHLC_COLUMNS = ['open', 'high', 'low', 'close'] as const;
const NON_SERIES_COLUMNS = new Set<string>(['time', 'ohlc_range', ...OHLC_COLUMNS]);

/** Custom SVG shape for a single candlestick bar.
 * Receives y/height from the stacked "ohlc_range" bar, which maps exactly
 * from the high pixel (y) to the low pixel (y + height). */
function CandleShape(props: any) {
    const { x, y, width, height, payload } = props;
    if (!payload || payload.open == null || payload.close == null || height <= 0) return null;

    const range = payload.high - payload.low || 1;
    const isBullish = payload.close >= payload.open;
    const color = isBullish ? 'var(--accent-success)' : 'var(--accent-danger)';

    // y = high pixel, y+height = low pixel (via stacked bar offset)
    const frac = (v: number) => (payload.high - v) / range;
    const openY = y + frac(payload.open) * height;
    const closeY = y + frac(payload.close) * height;
    const midX = x + width / 2;
    const bodyLeft = x + width * 0.2;
    const bodyRight = x + width * 0.8;
    const bodyTop = Math.min(openY, closeY);
    const bodyH = Math.max(Math.abs(closeY - openY), 1);

    return (
        <g>
            {/* Wick: full high–low span */}
            <line x1={midX} y1={y} x2={midX} y2={y + height} stroke={color} strokeWidth={1} />
            {/* Body: open–close span */}
            <rect x={bodyLeft} y={bodyTop} width={bodyRight - bodyLeft} height={bodyH}
                fill={color} stroke={color} strokeWidth={1} fillOpacity={isBullish ? 0.85 : 1} />
        </g>
    );
}

export function LineChartWidget({
    data,
    average,
    overlays = [],
    setOverlays,
    tunedResult = null,
    strategyName = "Original",
    categorical_labels,
    display_type,
    nature,
    globalStart,
    globalEnd
}: Props) {
    const isCandlestick = display_type === 'candlestick';
    const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
    const [showOverlayPanel, setShowOverlayPanel] = useState(false);
    const overlayTypes = useOverlayTypes();

    const handleLegendMouseEnter = (o: any) => setHoveredSeries(o.dataKey);
    const handleLegendMouseLeave = () => setHoveredSeries(null);

    const chartData = useMemo(() => {
        const map = new Map<string, any>();
        data.forEach(row => map.set(row.time, { ...row }));

        if (tunedResult) {
            tunedResult.forEach(row => {
                const t = row.time;
                if (!map.has(t)) map.set(t, { time: t });
                map.get(t)['tuned_preview'] = row.value;
            });
        }

        overlays.forEach((o, idx) => {
            o.result.data.forEach((row: any) => {
                const t = row.time;
                if (!map.has(t)) map.set(t, { time: t });
                Object.keys(row).forEach(k => {
                    if (k !== "time") {
                        map.get(t)[`${o.id}_${idx}_${k}`] = row[k];
                    }
                });
            });
        });

        const built = Array.from(map.values()).sort((a, b) => a.time.localeCompare(b.time));

        // Pre-compute ohlc_range for the stacked-bar technique (visible bar = high - low)
        if (isCandlestick) {
            built.forEach(row => {
                if (row.high != null && row.low != null) {
                    row.ohlc_range = row.high - row.low;
                }
            });
        }

        // Apply downsampling more aggressively for better snappy feel
        // Hourly data for 3-6 months (2000-4000 points) starts to lag Recharts.
        if (built.length > 1500) {
            return downsampleMinMax(built, 1000, isCandlestick ? 'high' : 'value');
        }

        return built;
    }, [data, overlays, tunedResult, isCandlestick]);

    const { seriesConfigs, leftDomain, rightDomain } = useMemo(() => {
        const empty = { seriesConfigs: {} as Record<string, string>, leftDomain: ['auto', 'auto'] as const, rightDomain: ['auto', 'auto'] as const };
        if (!chartData || chartData.length === 0) return empty;

        const statsByKey = new Map<string, { min: number; max: number }>();

        for (const row of chartData) {
            for (const [key, rawValue] of Object.entries(row)) {
                if (NON_SERIES_COLUMNS.has(key) || rawValue == null) continue;

                const value = Number(rawValue);
                if (!Number.isFinite(value)) continue;

                const existing = statsByKey.get(key);
                if (!existing) {
                    statsByKey.set(key, { min: value, max: value });
                    continue;
                }

                if (value < existing.min) existing.min = value;
                if (value > existing.max) existing.max = value;
            }
        }

        const mainStats = statsByKey.get(isCandlestick ? 'high' : 'value') ?? { min: 0, max: 0 };
        const mainMax = Math.abs(mainStats.max);

        const assignments: Record<string, string> = {};
        const seriesKeys = Array.from(statsByKey.keys());

        seriesKeys.forEach(key => {
            if (key === 'value' || key === 'tuned_preview') {
                assignments[key] = 'left';
                return;
            }
            const s = statsByKey.get(key) ?? { min: 0, max: 0 };
            const sMax = Math.abs(s.max);
            const ratio = mainMax > 0 && sMax > 0.01 ? mainMax / sMax : 1;
            assignments[key] = (ratio >= 10 || ratio <= 0.1) ? 'right' : 'left';
        });

        const needsSplit = Object.values(assignments).includes('right');
        if (!needsSplit) {
            return { seriesConfigs: assignments, leftDomain: ['auto', 'auto'] as const, rightDomain: ['auto', 'auto'] as const };
        }

        const Lmin = mainStats.min;
        const Lmax = mainStats.max;
        const Lrange = Math.max(Lmax - Lmin, 0.0001);
        const LdHeight = Lrange / 0.65;
        const leftDomain = [Lmax - LdHeight * 0.95, Lmax + LdHeight * 0.05];

        const rightKeys = seriesKeys.filter(k => assignments[k] === 'right');
        const Rmax = Math.max(...rightKeys.map(k => Math.abs((statsByKey.get(k) ?? { max: 0 }).max)), 0.0001);
        const rightDomain = [0, Rmax / 0.25];

        return { seriesConfigs: assignments, leftDomain, rightDomain };
    }, [chartData]);

    const needsRightAxis = Object.values(seriesConfigs).includes("right");

    // Shared formatters
    const formatLabel = (l: any) => {
        return getDateAggregationKey(l, "datetime");
    };

    const formatTick = (value: any) => {
        if (categorical_labels && typeof value === 'number' && categorical_labels[value]) {
            return categorical_labels[value].split(' ')[0];
        }
        return typeof value === 'number' ? value.toFixed(2) : value.toString();
    };

    const formatTooltipValue = (value: any, name: string, item: any) => {
        if (isCandlestick) {
            const p = item.payload;
            if (name === strategyName && p.open != null) {
                return [`O: ${p.open?.toFixed(4)} H: ${p.high?.toFixed(4)} L: ${p.low?.toFixed(4)} C: ${p.close?.toFixed(4)}`, name];
            }
        }
        if (categorical_labels && typeof value === 'number' && categorical_labels[value]) {
            return [categorical_labels[value], name];
        }
        return [typeof value === 'number' ? value.toFixed(4) : value, name];
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <div className="widget-header" style={{
                padding: "0.5rem 1rem",
                borderBottom: "1px solid var(--surface-glass-border)",
                backgroundColor: "var(--bg-card)",
                fontWeight: "bold",
                cursor: "move",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontSize: "0.9rem", opacity: 0.8, display: "flex", alignItems: "center", gap: "4px" }}>
                        📈 Line Chart
                    </span>
                    <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setShowOverlayPanel(v => !v); }}
                        style={{
                            fontSize: "0.75rem",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            background: showOverlayPanel ? "var(--bg-app)" : "var(--surface-glass)",
                            border: "1px solid var(--surface-glass-border)",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            transition: "all 0.2s ease"
                        }}
                    >
                        <span style={{ filter: showOverlayPanel ? "none" : "grayscale(1)" }}>🎨</span>
                        Overlays {overlays?.length ? `(${overlays.length})` : ""}
                    </button>
                </div>
            </div>

            {showOverlayPanel && (
                <div style={{
                    position: "absolute",
                    left: 0,
                    top: 40,
                    bottom: 0,
                    width: 260,
                    background: "var(--bg-panel)",
                    borderRight: "1px solid var(--surface-glass-border)",
                    boxShadow: "var(--shadow-sm)",
                    overflowY: "auto",
                    padding: "0.5rem",
                    zIndex: 1000,
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <h4 style={{ margin: 0 }}>Overlays ({overlays?.length ?? 0})</h4>
                        <button onClick={() => setShowOverlayPanel(false)} style={{ background: "transparent", border: "none", fontSize: "1.2rem", cursor: "pointer" }}>✖</button>
                    </div>
                    <OverlayManager
                        overlayTypes={overlayTypes}
                        overlays={overlays}
                        onAdd={(o) => setOverlays([...overlays, o])}
                        onUpdate={(idx, updated) => {
                            const next = [...overlays];
                            next[idx] = updated;
                            setOverlays(next);
                        }}
                        onRemove={(idx) => {
                            const next = [...overlays];
                            next.splice(idx, 1);
                            setOverlays(next);
                        }}
                        baseData={data || []}
                        globalStart={globalStart}
                        globalEnd={globalEnd}
                    />
                </div>
            )}

            <div style={{ flexGrow: 1, minHeight: 0, padding: '1rem' }}>
                <ResponsiveContainer width="100%" height="100%">
                    {/* A single ComposedChart handles both line and candlestick modes.
                        Candlestick-specific bars are rendered conditionally; all other
                        elements (axes, legend, tooltip, overlays) are shared. */}
                    <ComposedChart
                        data={chartData}
                    >
                        <Legend onMouseEnter={handleLegendMouseEnter} onMouseLeave={handleLegendMouseLeave} wrapperStyle={{ cursor: 'pointer' }} />
                        <XAxis dataKey="time" tickFormatter={(t) => getDateAggregationKey(t)} tick={{ fill: 'var(--text-muted)' }} />
                        <YAxis
                            yAxisId="left"
                            orientation="left"
                            domain={isCandlestick ? ['auto', 'auto'] : leftDomain}
                            tick={{ fill: 'var(--text-muted)' }}
                            tickFormatter={formatTick}
                        />
                        {needsRightAxis && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={rightDomain}
                                tick={{ fill: 'var(--text-muted)' }}
                                tickFormatter={formatTick}
                            />
                        )}
                        <Tooltip
                            contentStyle={{ background: 'var(--bg-panel)', border: '1px solid var(--surface-glass-border)', borderRadius: '8px' }}
                            labelFormatter={formatLabel}
                            formatter={formatTooltipValue}
                            animationDuration={0}
                        />
                        {nature !== 'result' && (
                            <ReferenceLine
                                y={average}
                                yAxisId="left"
                                stroke="var(--accent-danger)"
                                strokeDasharray="3 3"
                                label={{ value: "Avg", position: "top", fill: "var(--accent-danger)", fontSize: 12 }}
                                strokeOpacity={hoveredSeries ? 0.3 : 1}
                            />
                        )}

                        {/* ── Candlestick bars (only when display_type === 'candlestick') ── */}
                        {isCandlestick && (
                            <>
                                {/* Transparent spacer lifts the visible bar up to the 'low' price level */}
                                <Bar dataKey="low" yAxisId="left" stackId="candle" fill="transparent" stroke="none" legendType="none" isAnimationActive={false} />
                                {/* Visible bar spans low→high; CandleShape draws wick + body */}
                                <Bar dataKey="ohlc_range" yAxisId="left" stackId="candle" name={strategyName} shape={<CandleShape />} isAnimationActive={false} />
                            </>
                        )}

                        {/* ── Standard value line (only for non-candlestick) ── */}
                        {!isCandlestick && (
                            <>
                                <Line
                                    type={chartData.length > 800 ? "linear" : "monotone"}
                                    dataKey="value"
                                    stroke="var(--nature-price)"
                                    dot={false}
                                    name={strategyName}
                                    yAxisId={seriesConfigs['value'] || 'left'}
                                    strokeWidth={hoveredSeries === 'value' ? 3 : 1}
                                    strokeOpacity={hoveredSeries && hoveredSeries !== 'value' ? 0.3 : 1}
                                    isAnimationActive={false}
                                />
                                {tunedResult && (
                                    <Line
                                        type={chartData.length > 1000 ? "linear" : "monotone"}
                                        dataKey="tuned_preview"
                                        stroke="var(--accent-success)"
                                        strokeDasharray="5 5"
                                        dot={false}
                                        name="Tuned Preview"
                                        yAxisId="left"
                                        strokeWidth={hoveredSeries === 'tuned_preview' ? 3 : 1}
                                        strokeOpacity={hoveredSeries && hoveredSeries !== 'tuned_preview' ? 0.3 : 1}
                                        isAnimationActive={false}
                                    />
                                )}
                            </>
                        )}

                        {/* ── Overlays (shared by both modes) ── */}
                        {overlays.map((o, idx) => {
                            const cols = o.result.columns.filter((c: any) => c.id !== "time");
                            return cols.map((col: any) => {
                                const key = `${o.id}_${idx}_${col.id}`;
                                const stroke = o.color || o.result.display?.style?.color || getColor(idx);
                                if (o.result.display?.type === "arrow") {
                                    return <Scatter key={key} dataKey={key} fill={stroke} shape="triangle" />;
                                }
                                return (
                                    <Line
                                        key={key}
                                        type={chartData.length > 1000 ? "linear" : "monotone"}
                                        dataKey={key}
                                        stroke={stroke}
                                        dot={false}
                                        name={`${o.name} (${col.id})`}
                                        yAxisId={seriesConfigs[key] || 'left'}
                                        strokeWidth={hoveredSeries === key ? 3 : 1}
                                        strokeOpacity={hoveredSeries && hoveredSeries !== key ? 0.3 : 1}
                                        isAnimationActive={false}
                                    />
                                );
                            });
                        })}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

